<template>
  <div>
    <SettingsPagesEmptyState
      v-if="isEmpty(formModel.elements)"
      hero-height="128"
      hero-icon="no-groups-hero"
      hero-width="142"
    >
      <template #title> {{ $t('dashboard.no_elements_yet') }} </template>
      <template #subtitle> {{ $t('dashboard.you_dont_have_elements') }} </template>

      <template v-if="customDashboardState.userCanManageCustomDashboard && !isReadMode" #action>
        <AppButton
          height="24"
          icon="plus-next"
          type="primary-next"
          width="24"
          @click="showAddObjectivesModal"
        >
          {{ $t('create.elements.btn.add') }}
        </AppButton>
      </template>
    </SettingsPagesEmptyState>

    <template v-else>
      <div class="cdo-Header">
        <div class="cdo-ProgressWrapper">
          <span class="cdo-IndicatorProgress">{{ getIndicatorProgress + '%' }}</span>
          <span class="cdo-IndicatorSubtitle">{{ $t('dashboard.avg_progress') }}</span>
        </div>
        <div v-if="formModel.id && formModel.elements?.length" class="cdo-GradeChart">
          <DashboardChart
            :dashboard-item="dashboardItem"
            :external-chart-data="formModel.chartData"
            :module-id="formModel.id"
            :title="formModel.name"
            grade-color="primary-color-next"
            has-full-size-toggle
          />
        </div>
      </div>
      <IndicatorPanelWithProgress
        v-if="formModel.id"
        :indicator-width="getIndicatorProgress"
        background-color="rgba(var(--primary-color-rgb-next), 0.1)"
        class="cdc-IndicatorPanel"
        color="var(--primary-color-next)"
        style="--height: 16px; --border-radius: 24px; --border-radius-progress: 24px"
      />
      <div
        ref="hoverTarget"
        :style="{
          '--autoplay-duration': `${TIMEOUT}ms`
        }"
      >
        <div v-if="formModel.id">
          <!--   v-show needed in export, because AnimatedNumber has replacing content and numbers don't show     -->
          <CustomDashboardVerticalView
            v-show="showVerticalView"
            :elements="formModel.elements"
            :is-read-mode
            :is-readonly="isReadonly"
            @open-objective="onOpenObjectiveDetails"
            @open-checkin-modal="onOpenCheckinModal"
          />

          <CustomDashboardSliderView
            v-show="showSliderView"
            v-model:slide-index="slideIndex"
            :elements="formModel.elements"
            :is-cursor-outside="isCursorOutside"
            :is-read-mode
            :is-readonly="isReadonly"
            :is-run-carousel="isRunCarousel"
            :selected-item="selectedItem"
            :timeout="TIMEOUT"
            @select-item="selectItem"
            @open-objective="onOpenObjectiveDetails"
            @open-checkin-modal="onOpenCheckinModal"
          />
        </div>
        <div v-if="isObjectiveExist && selectedItemId && showSliderView" class="cdc-ModalContent">
          <CustomDashboardNestedItems
            :is-read-mode
            :objective="selectedItem"
            @open-objective="onOpenObjectiveDetails"
            @open-checkin-modal="onOpenCheckinModal"
          />
        </div>
      </div>
    </template>
  </div>
</template>
<script setup>
import { useMouseInElement } from '@vueuse/core'
import { isEmpty } from 'lodash'
import { computed, inject, ref } from 'vue'

import { HORIZONTAL, LAYOUT_VIEW_TYPES, VERTICAL } from '@/utils/custom-dashboard-helper'
import {
  customDashboardStateInjectionKey,
  currentModuleStateInjectionKey
} from '@/utils/injection-keys'
// import { OKR_TYPE_TO_FORM_VIEW } from '@/utils/objectives'

import DashboardChart from '@/components/dashboard/custom-dashboard/chart/DashboardChart'
import CustomDashboardNestedItems from '@/components/dashboard/custom-dashboard/CustomDashboardNestedItems'
import CustomDashboardSliderView from '@/components/dashboard/custom-dashboard/CustomDashboardSliderView'
import CustomDashboardVerticalView from '@/components/dashboard/custom-dashboard/CustomDashboardVerticalView'
import AppButton from '@/components/ui/AppButton/AppButton'
import IndicatorPanelWithProgress from '@/components/ui/IndicatorPanel/IndicatorPanelWithProgress'
import SettingsPagesEmptyState from '@/components/ui/SettingsPagesEmptyState/SettingsPagesEmptyState'

const TIMEOUT = 60000

defineOptions({
  name: 'CustomDashboardOkrModuleContent'
})

const props = defineProps({
  formModel: {
    type: Object,
    default: () => ({})
  },

  dashboardItem: {
    type: Object,
    default: () => ({})
  },

  isReadonly: {
    type: Boolean
  },

  isRunCarousel: {
    type: Boolean
  }
})
const customDashboardState = inject(customDashboardStateInjectionKey)

const isReadMode = computed(() => Boolean(customDashboardState.value.isReadMode))

const currentModuleState = inject(currentModuleStateInjectionKey, {})

const showAddObjectivesModal = () => {
  customDashboardState.value.addElementsToModule(props.formModel)
}

const isObjectiveExist = computed(() => {
  return props.formModel.id && selectedItem.value
})

const isHorizontalView = computed(() => {
  return (
    currentModuleState.value.currentLayoutTypeId === LAYOUT_VIEW_TYPES[HORIZONTAL] ||
    isEmpty(currentModuleState.value)
  )
})

const isVerticalView = computed(() => {
  return currentModuleState.value.currentLayoutTypeId === LAYOUT_VIEW_TYPES[VERTICAL]
})

const getIndicatorProgress = computed(() => {
  return isNaN(props.formModel.progress) ? 0 : Math.round(props.formModel.progress * 10) / 10
})

const showVerticalView = computed(() => {
  return currentModuleState.value.isExportProceed ? true : isVerticalView.value && props.isReadonly // isReadonly needed for export
})

const showSliderView = computed(() => {
  return currentModuleState.value.isExportProceed
    ? false
    : isHorizontalView.value || !props.isReadonly // isReadonly not needed in edit mode
})

const onOpenObjectiveDetails = async objective => {
  customDashboardState.value.editElement({
    element: objective
  })
}

const onOpenCheckinModal = payload => {
  customDashboardState.value.openCheckinModal(payload)
}

const selectedItem = computed(() => {
  return props.formModel.elements.find(item => item.id === selectedItemId.value)
})

const selectedItemId = ref(null)
const selectItem = (item, index) => {
  selectedItemId.value = item.id
  slideIndex.value = index
}

const hoverTarget = ref(null)
const { isOutside: isCursorOutside } = useMouseInElement(hoverTarget)

const slideIndex = ref(0)
</script>

<style lang="scss" scoped>
.cdo-GradeChart {
  width: 230px;
}

.cdo-ProgressWrapper {
  display: flex;
  flex-direction: column;
}

.cdo-Header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 16px 0;
}

.cdo-IndicatorProgress {
  font-weight: fw('bold');
  font-size: $fs-64;
  line-height: 72px;
  color: $dark-1;
}

.cdo-IndicatorSubtitle {
  font-weight: fw('medium');
  font-size: $fs-18;
  line-height: 24px;
}

.cdc-IndicatorPanel {
  margin: 0 0 20px 0;
}
</style>
